import React, { useState } from 'react';
import { learningAssetAPI } from '../services/api';

function FileUploader({ onUploadComplete, acceptedTypes = '*', maxSize = 500 }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);

  const extractVideoDuration = (file) => {
    return new Promise((resolve) => {
      // Only process video files
      if (!file.type.startsWith('video/')) {
        resolve(null);
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = Math.floor(video.duration); // Duration in seconds
        resolve(duration);
      };

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(null);
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (maxSize in MB)
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB`);
      return;
    }

    setSelectedFile(file);
    setError('');

    // Extract video duration if it's a video file
    if (file.type.startsWith('video/')) {
      const duration = await extractVideoDuration(file);
      setVideoDuration(duration);
    } else {
      setVideoDuration(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setProgress(0);
      setError('');

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await learningAssetAPI.uploadFile(formData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      });

      if (onUploadComplete) {
        onUploadComplete({
          ...response.data.file,
          duration: videoDuration // Pass video duration in seconds
        });
      }

      setSelectedFile(null);
      setVideoDuration(null);
      setProgress(0);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      background: '#2d3748',
      borderRadius: '8px',
      border: '1px solid #4a5568'
    }}>
      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
        Upload File
      </h4>

      <div style={{ marginBottom: '16px' }}>
        <input
          type="file"
          onChange={handleFileSelect}
          accept={acceptedTypes}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '10px',
            background: '#1a202c',
            border: '1px solid #4a5568',
            borderRadius: '6px',
            color: '#e2e8f0',
            fontSize: '13px'
          }}
        />
      </div>

      {selectedFile && (
        <div style={{
          marginBottom: '12px',
          padding: '10px',
          background: '#1a202c',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#a0aec0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>{selectedFile.name}</span>
            <span>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
          </div>
          <div style={{ fontSize: '12px', color: '#718096' }}>
            {selectedFile.type || 'Unknown type'}
          </div>
          {videoDuration && (
            <div style={{ fontSize: '12px', color: '#667eea', marginTop: '4px' }}>
              Duration: {Math.floor(videoDuration / 60)}:{(videoDuration % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      )}

      {uploading && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', color: '#a0aec0' }}>Uploading...</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#667eea' }}>
              {progress}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            background: '#1a202c',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      {error && (
        <div style={{
          padding: '8px 12px',
          background: '#fed7d7',
          color: '#c53030',
          borderRadius: '6px',
          fontSize: '13px',
          marginBottom: '12px'
        }}>
          {error}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!selectedFile || uploading}
        className="btn btn-primary"
        style={{
          width: '100%',
          opacity: !selectedFile || uploading ? 0.5 : 1,
          cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer'
        }}
      >
        {uploading ? `Uploading... ${progress}%` : 'Upload File'}
      </button>
    </div>
  );
}

export default FileUploader;
